import { format } from "date-fns";
import { webDownload } from "creso-core/hooks";
import { sanitizeFileName, type ZipArchive } from "../../utils/zip";
import type { ReportFamilyMember } from "./types";

export type FamilyCapitalGainsMember = {
  clientId: number;
  name: string;
  investmentAccount: string;
};

/** Members that can have a report generated — those with a client and an investment account. */
export function getFamilyCapitalGainsMembers(
  members: ReportFamilyMember[] = []
): FamilyCapitalGainsMember[] {
  return members.flatMap((member) =>
    member.client_id && member.investment_account_id
      ? [
          {
            clientId: member.client_id,
            name: member.display_name ?? member.name ?? "",
            investmentAccount: member.investment_account_id,
          },
        ]
      : []
  );
}

type GenerateReportFile = (payload: {
  file_type: "excel" | "csv";
  start_date: string;
  end_date: string;
  investment_account_guid: string;
  type: "capital_gains";
  source: "INTERNAL" | "EXTERNAL" | "OVERALL";
}) => Promise<{ report_url?: string }>;

/** PDF path: generate + add to zip via the hook downloader (no blob return needed). */
type GeneratePdf = (
  clientId: number,
  fromDate: string,
  toDate: string,
  fileName: string
) => Promise<void>;

export async function downloadFamilyCapitalGainsZip({
  familyName,
  members,
  selectedFy,
  fileType,
  selectedSource,
  zip,
  generateReportFile,
  generatePdf,
  onProgress,
}: {
  familyName: string;
  members: FamilyCapitalGainsMember[];
  selectedFy: number;
  fileType: "json" | "excel" | "csv";
  selectedSource: "INTERNAL" | "EXTERNAL" | "OVERALL";
  zip: ZipArchive;
  generateReportFile: GenerateReportFile;
  generatePdf: GeneratePdf;
  onProgress?: (current: number, total: number) => void;
}) {
  if (!members.length) {
    throw new Error("No members with investment accounts found");
  }

  const fromDate = format(new Date(selectedFy, 3, 1), "yyyy-MM-dd");
  const toDate = format(new Date(selectedFy + 1, 2, 31), "yyyy-MM-dd");

  await members.reduce<Promise<void>>(async (previous, member, index) => {
    await previous;
    onProgress?.(index + 1, members.length);

    const memberName = sanitizeFileName(member.name);

    if (fileType === "json") {
      await generatePdf(
        member.clientId,
        fromDate,
        toDate,
        `${memberName}-FY${selectedFy}-${selectedFy + 1}-capital-gains.pdf`
      );
      return;
    }

    const result = await generateReportFile({
      file_type: fileType,
      start_date: fromDate,
      end_date: toDate,
      investment_account_guid: member.investmentAccount,
      type: "capital_gains",
      source: selectedSource,
    });
    if (!result.report_url) {
      throw new Error("Failed to generate capital gains report");
    }
    const fileExtension = result.report_url.split(".").pop();
    const response = await fetch(
      `${result.report_url}?source=${selectedSource}&q=${new Date().toISOString()}`
    );
    if (!response.ok) {
      throw new Error("Failed to generate capital gains report");
    }
    const blob = await response.blob();
    zip.file(
      `${memberName}-${format(new Date(), "dd-MM-yyyy")}-capital-gains.${fileExtension}`,
      blob
    );
  }, Promise.resolve());

  const zipBlob = await zip.generateAsync({ type: "blob" });
  await webDownload(
    zipBlob,
    `${sanitizeFileName(familyName)}-FY${selectedFy}-${selectedFy + 1}-capital-gains.zip`
  );
}
